import { SkeletonLoader } from "@via/components/loading/loading";
import {
  useCreateAdminOrganizationRunEnvironment,
  useGetAdminOrganizationCredentials,
  useGetAdminOrganizationRunEnvironment,
  useUpdateAdminOrganizationRunEnvironment,
} from "@via/pages/account/admin/organizations/organizations.api.tsx";
import {
  RunEnvironmentFormView,
  type RunEnvironmentCredentialOption,
  type RunEnvironmentFormAdapter,
} from "@via/pages/organization/run-environments/run-environments-form.tsx";
import { type ProfileClusterForClient } from "common";
import { useEffect, type JSX } from "react";

/**
 * The organization run environment form, rendered for an organization the site administrator is
 * not a member of.
 *
 * Everything that differs from the organization page lives here rather than in the form: the
 * credentials offered are the TARGET organization's, from the admin directory endpoint, not the
 * signed-in administrator's; the writes go to the admin routes, which resolve ownership themselves
 * and never re-point it at the caller; and no connection test is supplied, which is what leaves
 * that control disabled (spec decision 7, a test runs as the credential's owner).
 *
 * An edit prefills from the full read route, not from the tab's listing: that listing is a lean
 * projection, and prefilling from it would leave every field it does not carry blank in a form
 * whose save would then write those blanks.
 */
export default function AdminRunEnvironmentForm({
  organizationId,
  runEnvironmentId,
  setOpen,
}: {
  organizationId: string;
  runEnvironmentId?: number;
  setOpen: (open: boolean) => void;
}): JSX.Element {
  const {
    data: credentials,
    isLoading: isCredentialsLoading,
    isError: isCredentialsError,
    refetch: refetchCredentials,
  } = useGetAdminOrganizationCredentials(organizationId, !!organizationId);

  const {
    data: existing,
    isLoading: isExistingLoading,
    isError: isExistingError,
  } = useGetAdminOrganizationRunEnvironment(organizationId, runEnvironmentId);

  const { mutateAsync: create, isPending: isCreating } =
    useCreateAdminOrganizationRunEnvironment();
  const { mutateAsync: update, isPending: isUpdating } =
    useUpdateAdminOrganizationRunEnvironment();

  const toOptions = (
    options: { id: number; name: string }[] | undefined
  ): RunEnvironmentCredentialOption[] =>
    (options || []).map(({ id, name }) => ({ label: name, value: id }));

  const adapter: RunEnvironmentFormAdapter = {
    credentialOptions: {
      amazon: toOptions(credentials?.amazon),
      google: toOptions(credentials?.google),
      ssh: toOptions(credentials?.ssh),
    },
    isCredentialOptionsLoading: isCredentialsLoading,
    credentialOptionsError: isCredentialsError,
    onRetryCredentialOptions: () => {
      void refetchCredentials();
    },
    // No testSshConnection on purpose. See the note above.
    isTestingSshConnection: false,
    save: async payload => {
      if (runEnvironmentId) {
        await update({ organizationId, runEnvironmentId, payload });
      } else {
        await create({ organizationId, payload });
      }
      setOpen(false);
    },
    isSaving: isCreating || isUpdating,
  };

  // An edit cannot start until the definition is in hand, or the form would render its defaults
  // over a real row and save them.
  useEffect(() => {
    if (runEnvironmentId && isExistingError) {
      setOpen(false);
    }
  }, [runEnvironmentId, isExistingError]);

  if (runEnvironmentId && (isExistingLoading || !existing)) {
    return (
      <div className="p-6">
        <SkeletonLoader count={6} />
      </div>
    );
  }

  return (
    <RunEnvironmentFormView
      setOpen={setOpen}
      editableRunEnvironment={existing as ProfileClusterForClient | undefined}
      adapter={adapter}
    />
  );
}
